import api from './api';

/**
 * The client half of `core/bulk_actions.py`'s `POST <resource>/bulk/`.
 *
 * The server answers PER ID rather than all-or-nothing, because a selection of
 * forty where three are still referenced should not fail entirely and leave the
 * person guessing which three. That shape has to survive to the screen intact:
 * reporting only "done" here would recreate the exact bug this platform is
 * prone to, an action that claims success while part of it silently did not
 * happen.
 */

export type BulkOperation = 'activate' | 'deactivate' | 'delete';

export interface BulkFailure {
  id: string;
  /** `not_found` | `referenced` | `not_supported` | `error` */
  reason: string;
  detail: string;
  /** For `referenced`: `{ "policies": 3 }` — what still points at the row. */
  blockers?: Record<string, number>;
}

export interface BulkResult {
  action: BulkOperation;
  requested: number;
  succeeded: string[];
  failed: BulkFailure[];
  /** Server-worded summary; stays true when some rows were refused. */
  detail: string;
}

/**
 * `resource` is the list path without a trailing slash, e.g.
 * `geography/states` or `vehicles/makes`.
 */
export async function runBulkAction(
  resource: string,
  ids: string[],
  action: BulkOperation
): Promise<BulkResult> {
  const path = `/${resource.replace(/^\/|\/$/g, '')}/bulk/`;
  const { data } = await api.post<BulkResult>(path, { ids, action });
  return data;
}

/** True when every requested row was acted on. */
export function isCompleteSuccess(result: BulkResult): boolean {
  return result.failed.length === 0 && result.succeeded.length === result.requested;
}

/**
 * One line for a toast, naming the obstacle when there was one.
 *
 * Deliberately does NOT collapse a partial failure into "done": the whole point
 * of the per-id response is that the person can see what did not happen.
 */
export function describeBulkResult(result: BulkResult): string {
  if (isCompleteSuccess(result)) return result.detail;

  const referenced = result.failed.filter((f) => f.reason === 'referenced');
  if (referenced.length) {
    const blockers = referenced
      .flatMap((f) => Object.entries(f.blockers ?? {}))
      .reduce<Record<string, number>>((acc, [label, count]) => {
        acc[label] = (acc[label] ?? 0) + count;
        return acc;
      }, {});
    const what = Object.entries(blockers)
      .map(([label, count]) => `${count} ${label}`)
      .join(', ');
    const noun = referenced.length === 1 ? 'record is' : 'records are';
    return `${result.detail} ${referenced.length} ${noun} still in use${what ? ` by ${what}` : ''}.`;
  }
  return result.detail;
}
